/**
 * Live Ingest Panel
 *
 * Real-time SSE-connected panel that displays ingested data as it flows
 * through a pipeline. Shows both raw upstream payload and mapped IKU data
 * side-by-side.
 *
 * Replaces the static one-shot PlaygroundTab with a persistent live view.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Stack,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as ClearIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
  DataObject as JsonIcon,
  Stream as StreamIcon,
  ArrowBack as ArrowLeftIcon,
} from '@mui/icons-material';
import { apiManagerApi } from './api';
import { buildEventSourcePath } from '@/lib/eventSourceAuth';

// ============================================================================
// Types
// ============================================================================

interface IngestEvent {
  id: string;
  kind: string;
  status: string;
  raw?: any;
  iku?: any;
  endpoint_id?: string;
  record_count?: number;
  ts: number;
  node_id?: string;
  error?: string;
  detail?: any;
}

interface LiveIngestPanelProps {
  pipelineId: string | null;
  onPipelineChange?: (id: string) => void;
}

// ============================================================================
// JSON Preview Component
// ============================================================================

function JsonPreview({ data, label, bgColor }: { data: any; label: string; bgColor: string }) {
  const [expanded, setExpanded] = useState(false);
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const preview = text.length > 200 ? text.slice(0, 200) + '...' : text;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <JsonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={600}>{label}</Typography>
      </Box>
      <Box
        sx={{
          bgcolor: bgColor,
          borderRadius: 1,
          p: 1,
          maxHeight: expanded ? 400 : 100,
          overflow: 'auto',
          transition: 'max-height 0.3s',
          cursor: 'pointer',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? text : preview}
      </Box>
    </Box>
  );
}

// ============================================================================
// Event Row Component
// ============================================================================

function EventRow({ event }: { event: IngestEvent }) {
  const [expanded, setExpanded] = useState(false);
  const isData = event.kind === 'data';
  const isError = event.status === 'error';
  const time = new Date(event.ts).toLocaleTimeString();

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: isError ? 'error.light' : isData ? 'success.light' : 'divider',
        borderLeftWidth: 3,
        borderLeftColor: isError ? 'error.main' : isData ? 'success.main' : 'primary.main',
      }}
    >
      {/* Summary line */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: isData ? 'pointer' : 'default',
        }}
        onClick={() => isData && setExpanded(!expanded)}
      >
        <Chip
          label={event.kind}
          size="small"
          color={isData ? 'success' : isError ? 'error' : 'default'}
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {event.endpoint_id && <>{event.endpoint_id} | </>}
          {event.status}
          {event.record_count != null && ` (${event.record_count} records)`}
          {event.node_id && ` [${event.node_id}]`}
          {event.error && ` - ${event.error}`}
        </Typography>
        <Typography variant="caption" color="text.disabled">{time}</Typography>
        {isData && (
          <IconButton size="small" sx={{ p: 0 }}>
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Expanded data view */}
      {isData && (
        <Collapse in={expanded}>
          <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 1 }}>
            {event.raw && <JsonPreview data={event.raw} label="Raw Payload" bgColor="grey.50" />}
            {event.iku && <JsonPreview data={event.iku} label="Mapped IKU" bgColor="rgba(46, 125, 50, 0.04)" />}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

// ============================================================================
// Stats Bar Component
// ============================================================================

function StatsBar({ events, connected }: { events: IngestEvent[]; connected: boolean }) {
  const dataEvents = events.filter((e) => e.kind === 'data');
  const errorEvents = events.filter((e) => e.status === 'error');
  const lastTs = events.length ? new Date(events[0].ts).toLocaleTimeString() : '--';

  // Events per second (rolling 30s window)
  const now = Date.now();
  const recent = dataEvents.filter((e) => now - e.ts < 30000);
  const eps = recent.length > 0 ? (recent.length / 30).toFixed(1) : '0.0';

  return (
    <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Tooltip title={connected ? 'Connected' : 'Disconnected'}>
        <DotIcon sx={{ fontSize: 12, color: connected ? 'success.main' : 'error.main' }} />
      </Tooltip>
      <Typography variant="caption" color="text.secondary">
        Events: <strong>{dataEvents.length}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Errors: <strong>{errorEvents.length}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Rate: <strong>{eps}/s</strong>
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
        Last: {lastTs}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Main LiveIngestPanel Component
// ============================================================================

export default function LiveIngestPanel({ pipelineId, onPipelineChange }: LiveIngestPanelProps) {
  const [events, setEvents] = useState<IngestEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const pausedBuffer = useRef<IngestEvent[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const eventIdCounter = useRef(0);

  // Fetch available pipelines for the selector dropdown
  useEffect(() => {
    apiManagerApi.listPipelines().then((resp) => {
      const p = resp?.data?.pipelines || resp?.data || [];
      if (Array.isArray(p)) setPipelines(p);
    }).catch(() => {});
  }, []);

  // Track external pipelineId prop
  useEffect(() => {
    if (pipelineId) setSelectedPipelineId(pipelineId);
  }, [pipelineId]);

  // SSE connection management
  const activePipelineId = selectedPipelineId || pipelineId;

  useEffect(() => {
    if (!activePipelineId) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setConnected(false);
      }
      return;
    }

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const connect = () => {
      if (cancelled || !activePipelineId) return;

      const sseUrl = buildEventSourcePath(
        `/cp/api-ingest/api/pipelines/${encodeURIComponent(activePipelineId)}/events`
      );
      if (!sseUrl) {
        setConnected(false);
        return;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const es = new EventSource(sseUrl, { withCredentials: false } as any);

    es.onopen = () => {
      setConnected(true);
    };

    const handlePipelineEvent = (e: MessageEvent<string>) => {
      try {
        const data = JSON.parse(e.data);
        const event: IngestEvent = {
          id: `evt-${++eventIdCounter.current}`,
          kind: data.kind || 'status',
          status: data.status || 'unknown',
          raw: data.raw,
          iku: data.iku,
          endpoint_id: data.endpoint_id,
          record_count: data.record_count,
          ts: data.ts || Date.now(),
          node_id: data.node_id,
          error: data.error,
          detail: data.detail,
        };

        if (paused) {
          pausedBuffer.current.push(event);
        } else {
          setEvents((prev) => [event, ...prev].slice(0, 200));
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    // The backend emits named SSE messages (`event: pipeline_event`), so
    // subscribe explicitly rather than relying only on the default message event.
    es.onmessage = handlePipelineEvent;
    es.addEventListener('pipeline_event', handlePipelineEvent as EventListener);

    es.addEventListener('connected', (e: any) => {
      setConnected(true);
    });

    es.addEventListener('heartbeat', () => {
      // Keep-alive, no action needed
    });

    es.onerror = () => {
      setConnected(false);
      es.close();
      eventSourceRef.current = null;
      if (!cancelled) {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };

    eventSourceRef.current = es;
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePipelineId]);

  // Flush paused buffer on resume
  useEffect(() => {
    if (!paused && pausedBuffer.current.length > 0) {
      setEvents((prev) => [...pausedBuffer.current.reverse(), ...prev].slice(0, 200));
      pausedBuffer.current = [];
    }
  }, [paused]);

  // Manual pipeline run
  const handleManualRun = async () => {
    if (!activePipelineId) return;
    try {
      await apiManagerApi.runPipeline(activePipelineId);
    } catch (e: any) {
      console.error('Manual pipeline run failed:', e);
    }
  };

  // Clear events
  const handleClear = () => {
    setEvents([]);
    pausedBuffer.current = [];
  };

  // Pipeline selector change
  const handlePipelineSelect = (id: string) => {
    setSelectedPipelineId(id);
    setEvents([]);
    if (onPipelineChange) onPipelineChange(id);
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  // Empty state: no pipeline selected
  if (!activePipelineId) {
    return (
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 600,
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <StreamIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" textAlign="center">
          Live Data Stream
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ maxWidth: 300, mb: 2 }}>
          Set up an API in the chat to see live data here, or select an existing pipeline below.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ArrowLeftIcon sx={{ color: 'primary.main' }} />
          <Typography variant="body2" color="primary.main">
            Start in the chat panel
          </Typography>
        </Box>

        {pipelines.length > 0 && (
          <FormControl size="small" sx={{ mt: 2, minWidth: 250 }}>
            <InputLabel>Or select a pipeline</InputLabel>
            <Select
              value={selectedPipelineId}
              label="Or select a pipeline"
              onChange={(e) => handlePipelineSelect(e.target.value)}
            >
              {pipelines.map((p: any) => (
                <MenuItem key={p.pipeline_id || p._id} value={p.pipeline_id || p._id}>
                  {p.api_id} / {p.endpoint_id} {p.enabled ? '' : '(disabled)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Paper>
    );
  }

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <StreamIcon sx={{ color: connected ? 'success.main' : 'text.disabled' }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          Live Data
        </Typography>
        <Chip
          icon={<DotIcon sx={{ fontSize: '10px !important' }} />}
          label={connected ? 'Connected' : 'Disconnected'}
          size="small"
          color={connected ? 'success' : 'default'}
          variant="outlined"
        />
      </Box>

      {/* Controls */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        {pipelines.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedPipelineId}
              displayEmpty
              onChange={(e) => handlePipelineSelect(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="" disabled>Select pipeline</MenuItem>
              {pipelines.map((p: any) => (
                <MenuItem key={p.pipeline_id || p._id} value={p.pipeline_id || p._id}>
                  {p.api_id}/{p.endpoint_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <Tooltip title={paused ? 'Resume' : 'Pause'}>
            <IconButton size="small" onClick={() => setPaused(!paused)} color={paused ? 'warning' : 'default'}>
              {paused ? <PlayIcon /> : <PauseIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={handleClear}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Manual Run">
            <IconButton size="small" onClick={handleManualRun} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Paused banner */}
      {paused && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Stream paused. {pausedBuffer.current.length} events buffered.
        </Alert>
      )}

      {/* Events area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">
              Waiting for data events...
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Trigger a pipeline run or wait for the next scheduled execution
            </Typography>
          </Box>
        )}

        {events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </Box>

      {/* Stats bar */}
      <StatsBar events={events} connected={connected} />
    </Paper>
  );
}
